import React, { useEffect, useMemo, useState } from 'react'
import ImagesViewer from './ImagesViewer'
import { Box, CircularProgress, Grid, Paper, Typography, TextField, Button, Divider } from '@mui/material';
import TitleBar from 'components/TitleBar';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from "yup";
import { useGetCustomerDashboardDCommentListQuery, useCreateCustomerDashboardCommentMutation } from 'store/slices/apis/customerDashboardSlice';
import { useDispatch } from "store";
import { openSnackbar, closeSnackbar } from "store/slices/snackbar";
import moment from 'moment';

const styles = {
    height: 420,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid #ddd",
    borderRadius: 2,
    bgcolor: "#f9fafb",
    color: "#6b7280",
    fontSize: 16,
    fontWeight: 500,
};

const CommentsAndRemarks = ({ type = "nhlml" }) => {
    const { projectId } = useParams();
    const dispatch = useDispatch();

    const [formData, setFormData] = useState({
        comment: "",
        projectId: projectId,
    });

    const [createCustomerDashboardComment, { isLoading }] = useCreateCustomerDashboardCommentMutation();

    const { data: commentsData } = useGetCustomerDashboardDCommentListQuery({ projectId: projectId, UserType: type });
    const comments = commentsData?.data || [];

    const commentSchema = Yup.object({
        comment: Yup.string().trim().required("Please enter comment"),
    });

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {

        let reqBody = {
            projectId: projectId,
            comment: values.comment,
            UserType: type
        };

        try {
            const response = await createCustomerDashboardComment({ body: reqBody }).unwrap();
            openNotification("Comment created successfully", "success");
            resetForm();
            setSubmitting(false);
        } catch (error) {
            openNotification(error?.error ?? "Something went wrong", "error");
        } finally {
            setSubmitting(false);
        }
    };

    const openNotification = (msg, type = "error", duration = 3000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: {
                    color: type
                },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    return (
        <Paper sx={{
            width: "100%",
            pb: 2,
            borderRadius: 2,
            overflow: "auto",
            border: "1px solid #eef0f4",
            boxSizing: "border-box"
        }}>
            <Grid container>
                <TitleBar
                    title="Comments/Remarks"
                    description="Check out your comments/remarks here."
                />
            </Grid>
            <Grid size={12} container spacing={1.5} sx={{ p: 2 }}>
                <Grid size={5}>
                    <Paper
                        sx={{
                            width: "100%",
                            p: 1.5,
                            borderRadius: 2,
                            overflow: "auto",
                            border: "1px solid #eef0f4",
                            boxSizing: "border-box",
                            height: "100%",
                            maxHeight: "420px"
                        }}
                    >
                        <Typography sx={{
                            fontFamily: "Outfit, sans-serif",
                            fontWeight: 600,
                            fontSize: '20px',
                            letterSpacing: "0px",
                            mb: 0,
                            color: "#111827",
                        }}>
                            Submit Comments
                        </Typography>
                        <Divider sx={{ mt: 2, mb: 4 }} />
                        <Formik
                            initialValues={formData}
                            validationSchema={commentSchema}
                            onSubmit={handleSubmit}
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleChange,
                                handleBlur,
                                handleSubmit,
                                isSubmitting,
                                isValid,
                                dirty
                            }) => (
                                <form onSubmit={handleSubmit}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            borderRadius: 2,
                                            gap: 2
                                        }}
                                    >
                                        <TextField
                                            name="comment"
                                            placeholder="Enter comment/remark"
                                            multiline
                                            rows={7}
                                            fullWidth
                                            value={values.comment}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                        {touched.comment && errors.comment && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.comment}
                                            </Typography>
                                        )}

                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disabled={!dirty || !isValid || isSubmitting}
                                            sx={{
                                                backgroundColor: "#00344C",
                                                borderRadius: "25px",
                                                textTransform: "none",
                                                fontWeight: 500,
                                                fontSize: "14px",
                                                lineHeight: "100%",
                                                fontFamily: "Outfit, sans-serif",
                                                color: "#FFFFFF",
                                                px: "20px",
                                                py: "15px",
                                                alignSelf: "flex-end"
                                            }}
                                        >
                                            {/* {isSubmitting ? "Saving..." : "Save"} */}
                                            Save
                                        </Button>

                                    </Box>
                                </form>
                            )}
                        </Formik>
                    </Paper>
                </Grid>
                <Grid size={7}>
                    {comments.length === 0 && (
                        <Box sx={styles}>
                            <Typography sx={{ textAlign: "center" }}>
                                No comments yet.
                            </Typography>
                        </Box>
                    )}
                    {comments.length > 0 && (
                        <Box sx={{ border: "1px solid #ddd", height: 420, borderRadius: 2 }}>
                            <Box
                                sx={{
                                    maxHeight: "420px",
                                    overflowY: "auto",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1.5
                                }}
                            >
                                {comments.map((item, index) => (
                                    <Paper
                                        key={item.customerDashboardCommentId}
                                        sx={{
                                            p: 1.5,
                                            border: "1px solid #eef0f4",
                                            borderRadius: 1
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                mb: 0.5
                                            }}
                                        >
                                            <Typography sx={{ color: "#5F5F5F", fontWeight: 600 }}>
                                                {index + 1}: {item.createdBy}
                                            </Typography>

                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {item?.createdDate ? moment(item.createdDate).format('lll') : null}  ({item.timeAgo})
                                            </Typography>
                                        </Box>

                                        <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                                            {item.comment}
                                        </Typography>
                                    </Paper>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Grid>
            </Grid>
        </Paper>
    )
}

export default CommentsAndRemarks
